import { useEffect, useState } from "react";
import { X, ExternalLink, Copy, Check, AlertCircle } from "lucide-react";
import { restaurantInfo } from "../mock";
import { useLang } from "../context/LanguageContext";
import { POPUP_STRINGS } from "../i18n/strings";
import { trackEvent } from "../utils/analytics";

const STORAGE_KEY = "ninas_welcome_seen_v1";
const SHOW_DELAY_MS = 3500;
const ORDER_URL = restaurantInfo.externalLinks.orderOnline;
const PROMO_CODE = "5OFFLINE";

export default function WelcomePopup() {
  const { lang } = useLang();
  const es = lang === "es";
  const p = (key) => (es ? POPUP_STRINGS[key].es : POPUP_STRINGS[key].en);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (localStorage.getItem(STORAGE_KEY)) return;
    } catch (e) { /* private mode */ }
    const t = setTimeout(() => {
      setOpen(true);
      trackEvent("welcome_popup_shown");
    }, SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch (e) { /* ignore */ }
    setOpen(false);
    trackEvent("welcome_popup_dismiss");
  };

  const handleCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(PROMO_CODE);
      setCopied(true);
      trackEvent("welcome_popup_code_copy", { code: PROMO_CODE });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) { /* clipboard blocked */ }
  };

  const goToOrder = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch (e) { /* ignore */ }
    trackEvent("welcome_popup_accept", { code: PROMO_CODE });
    window.open(ORDER_URL, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-popup-title"
      data-testid="welcome-popup"
    >
      {/* Backdrop */}
      <button
        onClick={dismiss}
        aria-label="Close welcome offer"
        className="absolute inset-0 bg-granite-dark/70 backdrop-blur-sm cursor-default"
        data-testid="welcome-popup-backdrop"
      />

      {/* Card — entire card is clickable to order online */}
      <div
        onClick={goToOrder}
        data-testid="welcome-popup-card"
        className="relative w-full max-w-md bg-cream rounded-3xl shadow-2xl overflow-hidden cursor-pointer group hover:shadow-[0_20px_60px_rgba(88,122,63,0.4)] transition-shadow animate-popup-in"
      >
        {/* Close btn — stops propagation so clicking X doesn't open ordering */}
        <button
          onClick={(e) => { e.stopPropagation(); dismiss(); }}
          aria-label="Close"
          data-testid="welcome-popup-close-btn"
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-cream/90 hover:bg-cream flex items-center justify-center text-granite hover:text-brand-dark border border-granite/10 shadow-md transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Top brand strip with decorative blobs */}
        <div className="relative bg-brand text-cream p-6 md:p-8 overflow-hidden">
          <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-brand-light/25 pointer-events-none" />
          <div className="absolute -bottom-20 -left-12 w-40 h-40 rounded-full bg-brand-dark/30 pointer-events-none" />

          <div className="relative">
            <div className="font-hand text-2xl text-brand-light mb-1">{p("welcome")}</div>
            <h2 id="welcome-popup-title" className="font-display text-3xl md:text-4xl font-semibold leading-tight">
              {p("save")}
            </h2>
            <p className="text-cream/85 text-sm mt-3 leading-relaxed">
              {p("useCode")}
            </p>
          </div>
        </div>

        {/* Promo code block */}
        <div className="px-6 md:px-8 pt-6 pb-2">
          <div className="text-[11px] uppercase tracking-[0.2em] text-granite/60 mb-2">{p("yourCode")}</div>
          <div className="flex items-center gap-3">
            <div
              data-testid="welcome-popup-promo-code"
              className="flex-1 font-display text-3xl md:text-4xl font-bold text-brand-dark tracking-[0.15em] py-3 px-4 rounded-xl bg-brand/10 border-2 border-dashed border-brand/40 text-center"
            >
              {PROMO_CODE}
            </div>
            <button
              onClick={handleCopy}
              aria-label="Copy code"
              data-testid="welcome-popup-copy-btn"
              className="w-12 h-12 rounded-xl bg-granite-dark hover:bg-granite text-cream flex items-center justify-center shrink-0 transition-colors"
            >
              {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            </button>
          </div>
          <div className="text-xs text-brand-dark mt-2 min-h-[16px]">
            {copied ? p("copied") : ""}
          </div>
        </div>

        {/* CTA */}
        <div className="px-6 md:px-8 pt-2">
          <div
            data-testid="welcome-popup-cta"
            className="w-full inline-flex items-center justify-center gap-2 bg-brand text-cream group-hover:bg-brand-dark px-6 py-4 rounded-full font-semibold text-[15px] transition-colors shadow-lg"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cream/70 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-cream" />
            </span>
            {p("cta")}
            <ExternalLink className="w-4 h-4" />
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mx-6 md:mx-8 mt-5 mb-3 p-4 rounded-2xl bg-cream-100 border border-brand/20">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-brand-dark shrink-0 mt-0.5" />
            <div className="text-[13px] text-granite-dark leading-relaxed">
              <div className="text-granite/75 mb-1.5">
                {p("disclaimer1")}
              </div>
              <div className="flex flex-wrap items-baseline gap-x-1.5">
                <strong className="text-brand-dark">{p("disclaimer2")}</strong>
                <a
                  href={ORDER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  data-testid="welcome-popup-inline-link"
                  className="font-semibold text-brand-dark underline decoration-brand decoration-2 underline-offset-2 hover:decoration-brand-dark break-all"
                >
                  order.taqueriadenina.com
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Dismiss link */}
        <div className="px-6 md:px-8 pb-6 md:pb-7">
          <button
            onClick={(e) => { e.stopPropagation(); dismiss(); }}
            data-testid="welcome-popup-dismiss-link"
            className="w-full text-center text-xs text-granite/50 hover:text-granite py-1"
          >
            {p("dismiss")}
          </button>
        </div>
      </div>
    </div>
  );
}
